import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Stack,
  TableContainer,
  Button,
  Tooltip,
  LinearProgress,
  Chip,
  Avatar,
  AvatarGroup,
  Pagination,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import MetaChips from '../Components/overflowChips.js';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import BackupIcon from '@mui/icons-material/Backup';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TaskIcon from '@mui/icons-material/Task';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TagIcon from '@mui/icons-material/Tag';
import { styled } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import api from '../api';
import { useAuth } from '../context/userContext';
import { toast } from 'react-toastify';
import UploadSheetDialog from './UploadSheetDialog.js';

const getErrMsg = err =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  (Array.isArray(err?.response?.data) ? err.response.data.join(', ') : '') ||
  err.message ||
  'Something went wrong';

const Frame = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[400]}`,
  padding: theme.spacing(3),
  background: '#fff'
}));

const normalizeKey = k => (k || '').toString().replace(/[\s_]+/g, '').toLowerCase();
const isCompanyNameKey = k => normalizeKey(k) === 'companyname';
const splitVals = v =>
  Array.isArray(v) ? v.filter(Boolean)
    : typeof v === 'string' ? v.split(/[;,]\s*/).map(s => s.trim()).filter(Boolean)
      : [];
const joinVals = arr => (arr || []).filter(Boolean).join('; ');

const excelSerialToMDY = n => {
  if (n instanceof Date) {
    const mm = n.getMonth() + 1;
    const dd = n.getDate();
    const yyyy = n.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }
  if (typeof n !== 'number') return n;
  const d = new Date(Date.UTC(1899, 11, 30) + n * 86400000);
  const mm = d.getMonth() + 1;
  const dd = d.getDate();
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const getDomain = url => {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return ''; }
};
const faviconFor = url => `https://www.google.com/s2/favicons?sz=64&domain=${encodeURIComponent(getDomain(url) || 'example.com')}`;
const initials = (name = '') => name.split(/\s+/).filter(Boolean).map(s => s[0].toUpperCase()).slice(0, 2).join('');
const daysBetween = (a, b) => Math.round((b - a) / 86400000);
const clamp = (n, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, n));
const criticalityColor = lvl => (lvl === 'High' ? 'error' : lvl === 'Medium' ? 'warning' : 'success');

const deadlineStatus = (issueDate, deadline) => {
  if (!deadline) return { label: 'No deadline', color: 'default' };
  const today = new Date();
  const d = new Date(deadline);
  const diff = daysBetween(today, d);
  if (diff < 0) return { label: 'Overdue', color: 'error' };
  if (diff <= 7) return { label: `Due soon (${diff}d)`, color: 'warning' };
  return { label: `On track (${diff}d left)`, color: 'success' };
};

const timeProgressPct = (issueDate, deadline) => {
  if (!issueDate || !deadline) return null;
  const start = new Date(issueDate).getTime();
  const end = new Date(deadline).getTime();
  const now = Date.now();
  if (end <= start) return 100;
  return clamp(((now - start) / (end - start)) * 100);
};

const toValidDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const normalizeDateArray = (value) => {
  if (Array.isArray(value)) return value.map(toValidDate).filter(Boolean);
  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return [];
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed.map(toValidDate).filter(Boolean);
    } catch { }
    return s.split(',').map(x => x.trim()).filter(Boolean).map(toValidDate).filter(Boolean);
  }
  const one = toValidDate(value);
  return one ? [one] : [];
};

const normalizeStringArray = (value) => {
  if (Array.isArray(value)) return value.map(x => String(x || '').trim()).filter(Boolean);
  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return [];
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed.map(x => String(x || '').trim()).filter(Boolean);
    } catch { }
    return s.split(',').map(x => x.trim()).filter(Boolean);
  }
  return value ? [String(value).trim()].filter(Boolean) : [];
};

export default function LegalUpdateDetail({
  open,
  onClose,
  update,
  onEdit = () => { },
  onRefresh = () => { },
}) {
  const [{ user }] = useAuth();
  const isAdmin = user?.role === 'admin';

  const fileInput = useRef();
  const sheetInput = useRef();

  const [sheetRows, setSheetRows] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [createFlow, setCreateFlow] = useState(false);
  const [locations, setLocation] = useState([]);
  const [owners, setOwners] = useState([]);
  const [managers, setManagers] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [parsing, setParsing] = useState(false);
  const [companyId, setCompanyId] = useState(null);

  const [busy, setBusy] = useState(false);
  const DEFAULT_PAGE_SIZE = 200;
  const [rowMeta, setRowMeta] = useState({ page: 1, limit: DEFAULT_PAGE_SIZE, totalRows: 0 });

  const [luTrackers, setLuTrackers] = useState([]);
  const [luTrackersLoading, setLuTrackersLoading] = useState(false);
  const [luTrackersMeta, setLuTrackersMeta] = useState({ page: 1, limit: 10, totalRows: 0 });

  const updateId = update?._id || null;

  const uniq = arr => Array.from(new Set((arr || []).filter(Boolean).map(s => String(s).trim())));
  const splitTokens = v => String(v).split(/[;,]/).map(s => s.trim()).filter(Boolean);

  const flattenLocations = (loc) => {
    if (!loc) return [];
    const out = [];
    if (Array.isArray(loc)) {
      for (const v of loc) out.push(...splitTokens(v));
      return uniq(out);
    }
    if (typeof loc === 'object') {
      for (const val of Object.values(loc)) {
        if (Array.isArray(val)) for (const v of val) out.push(...splitTokens(v));
        else out.push(...splitTokens(val));
      }
      return uniq(out);
    }
    if (typeof loc === 'string') return uniq(splitTokens(loc));
    return [];
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('detail/userinfo?view=meta');
        setCompanyId(data?.companyDetails?._id || null);
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    })();
  }, []);

  useEffect(() => {
    if (!open || !companyId) return;
    (async () => {
      try {
        const response = await api.post('company-detail/companyinfo', { id: companyId });
        const { data } = await api.get('detail/userinfo?view=meta');
        const locRaw = data?.companyDetails?.locations ?? data?.companyDetails?.Location ?? [];
        setLocation(flattenLocations(locRaw));
        const payload = response.data?.json || response.data || {};
        const { managers = [], owners = [], companyName: name } = payload;
        setManagers(managers);
        setOwners(owners);
        setCompanyName(name || '');
      } catch (e) {
        console.error("[LegalUpdateDetail] Error fetching company info:", e);
      }
    })();
  }, [open, companyId]);

  const loadLegalUpdateTrackers = async (page = 1, limit = luTrackersMeta.limit) => {
    if (!updateId) return;
    try {
      setLuTrackersLoading(true);
      const { data } = await api.get(`/tracker/by-legal-update/${updateId}`, { params: { page, limit } });
      setLuTrackers(data?.rows || []);
      setLuTrackersMeta({
        page: data?.page || page,
        limit: data?.limit || limit,
        totalRows: data?.totalRows || 0
      });
    } catch (err) {
      toast.error(getErrMsg(err));
    } finally {
      setLuTrackersLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !updateId) return;
    loadLegalUpdateTrackers(1, luTrackersMeta.limit);
  }, [open, updateId]);

  const handleDownload = async doc => {
    if (!updateId) return;
    try {
      const key = doc.filePath || (doc.url ? new URL(doc.url).pathname.replace(/^\/+/, '') : '');
      if (!key) return toast.error('No file key found');
      const url = `/s3/legal-updates/${updateId}/download/${encodeURIComponent(key)}`;
      const { data } = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([data], { type: doc.fileType || 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.fileName;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error(getErrMsg(err));
    }
  };

  const handleDelete = async doc => {
    if (!updateId) return;
    if (!window.confirm(`Delete ${doc.fileName}?`)) return;
    try {
      await api.delete(`s3/legal-updates/${updateId}/doc/${encodeURIComponent(doc.filePath)}`);
      onRefresh(updateId);
    } catch (err) {
      toast.error(getErrMsg(err));
    }
  };

  const handleNewDocs = async e => {
    if (!updateId) return;
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      const fd = new FormData();
      fd.append('updateId', updateId);
      files.forEach(f => fd.append('documents', f));
      await api.post('/s3/legal-updates/upload', fd);
      onRefresh(updateId);
    } catch (err) {
      toast.error(getErrMsg(err));
    }
  };

  const handleSheetUpload = e => {
    if (!updateId) return;
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);

    const fr = new FileReader();
    fr.onload = async ev => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array', cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        let json = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false });

        json = json.map(r => {
          const out = {};
          for (const [k, v] of Object.entries(r)) {
            if (isCompanyNameKey(k)) continue;
            out[k] = v;
          }
          return out;
        });

        const ownerKey = Object.keys(json[0] || {}).find(k => normalizeKey(k) === 'trackerowner') || 'trackerOwner';
        const managerKey = Object.keys(json[0] || {}).find(k => normalizeKey(k) === 'trackermanager') || 'trackerManager';
        const dueKey = Object.keys(json[0] || {}).find(k => normalizeKey(k) === 'duedate');

        const firstOwner = (owners[0]?.email || owners[0] || '') ?? '';
        const firstManager = (managers[0]?.email || managers[0] || '') ?? '';

        const parsedRows = json.map(r => {
          const hasOwner = Object.keys(r).some(k => normalizeKey(k) === 'trackerowner');
          const hasManager = Object.keys(r).some(k => normalizeKey(k) === 'trackermanager');

          const ownerVal = hasOwner ? r[Object.keys(r).find(k => normalizeKey(k) === 'trackerowner')] : firstOwner;
          const managerVal = hasManager ? r[Object.keys(r).find(k => normalizeKey(k) === 'trackermanager')] : firstManager;

          const normalizedOwner = joinVals(splitVals(ownerVal));
          const normalizedManager = joinVals(splitVals(managerVal));

          const out = {
            ...r,
            [ownerKey]: normalizedOwner,
            [managerKey]: normalizedManager,
            sourceUrl: `${window.location.origin}/legal-update/${updateId}`
          };

          if (dueKey && out[dueKey] !== undefined) out[dueKey] = excelSerialToMDY(out[dueKey]);
          return out;
        });

        await api.post('/draft-tracker/task-drafts', { legalUpdate: updateId, rows: parsedRows });

        const { data: saved } = await api.get(`/draft-tracker/task-drafts/${updateId}`);
        setSheetRows(saved?.rows || []);
        setCreateFlow(false);
        setPreviewOpen(true);
      } catch {
        toast.error('Could not read Excel file');
      } finally {
        setParsing(false);
      }
    };

    fr.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const formatDueDateFromIso = iso => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return excelSerialToMDY(d);
  };

  function toBool(v) {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      if (['true', 'yes', 'y', '1'].includes(s)) return true;
      if (['false', 'no', 'n', '0', ''].includes(s)) return false;
    }
    return false;
  }

  async function createTaskFromDraft() {
    if (!updateId) return;

    try {
      setBusy(true);

      const { data } = await api.get(`/draft-tracker/task-drafts/${updateId}`, {
        params: { page: 1, limit: rowMeta.limit }
      });

      const draftRows = data?.rows || [];

      setRowMeta({
        page: data?.page || 1,
        limit: data?.limit || rowMeta.limit,
        totalRows: data?.totalRows || 0
      });

      if (draftRows.length === 0) {
        setSheetRows([]);
        setCreateFlow(true);
        setPreviewOpen(true);
        return;
      }

      const rows = draftRows.map(r => ({
        ...r,
        proof: toBool(r.proof),
        companyname: companyName || ''
      }));

      setSheetRows(rows);
      setCreateFlow(true);
      setPreviewOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not load draft rows');
    } finally {
      setBusy(false);
    }
  }

  const showLinks = (update?.relatedURLs || []).length > 0;
  const showDocs = (update?.documents || []).length > 0;

  const myCompanyId = user?.company;
  const myAssignees = update?.assigneesByCompany?.[myCompanyId] || [];

  const luTrackerPages = Math.max(1, Math.ceil((luTrackersMeta.totalRows || 0) / (luTrackersMeta.limit || 10)));

  if (!update) return null;

  const {
    _id,
    title,
    issueDate,
    criticalityLevel,
    complianceDeadlines,
    lawAreas = [],
    contentType,
    summary,
    details,
    relatedURLs = [],
    documents = [],
    tags = [],
    updateNumbers,
  } = update;

  const deadlinesArr = normalizeDateArray(complianceDeadlines).sort((a, b) => a - b);
  const updateNumsArr = normalizeStringArray(updateNumbers);

  const effectiveDeadline = (() => {
    if (!deadlinesArr.length) return null;
    const now = Date.now();
    const upcoming = deadlinesArr.find(d => d.getTime() >= now);
    return upcoming || deadlinesArr[deadlinesArr.length - 1];
  })();

  const deadlineMeta = deadlineStatus(issueDate, effectiveDeadline);
  const progress = timeProgressPct(issueDate, effectiveDeadline);
  const daysLeft = effectiveDeadline ? daysBetween(new Date(), effectiveDeadline) : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle sx={{ pl: 3, pr: 1 }}>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Typography variant='h6' fontWeight={700}>Legal Update</Typography>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Button
              size='small'
              variant='contained'
              startIcon={<TaskIcon />}
              onClick={createTaskFromDraft}
              disabled={busy}
            >
              {busy ? 'Opening…' : 'Create Task'}
            </Button>

            {isAdmin && (
              <>
                <Button
                  size='small'
                  variant='outlined'
                  startIcon={<UploadFileIcon />}
                  onClick={() => sheetInput.current?.click()}
                >
                  Upload Task Sheet
                </Button>
                <input
                  ref={sheetInput}
                  hidden
                  accept='.xlsx,.xls'
                  type='file'
                  onChange={handleSheetUpload}
                />
              </>
            )}

            <Tooltip title='Copy share link'>
              <IconButton
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(`${window.location.origin}/legal-update/${_id}`);
                    toast.success('Link copied');
                  } catch {
                    toast.error('Could not copy link');
                  }
                }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>

            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ background: '#F9FAFB' }}>
        {parsing && <LinearProgress sx={{ mb: 1 }} />}

        <Frame>
          <MetaChips
            contentType={contentType}
            criticalityLevel={criticalityLevel}
            criticalityColor={criticalityColor}
            updateNumber={updateNumsArr[0] || ''}
            updateNumbers={updateNumsArr}
            issueDate={issueDate}
            complianceDeadline={effectiveDeadline}
            complianceDeadlines={deadlinesArr}
            deadlineMeta={deadlineMeta}
          />

          {typeof progress === 'number' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant='caption' sx={{ display: 'block', mb: 0.5 }}>
                Time elapsed {Math.round(progress)}%
                {Number.isFinite(daysLeft)
                  ? ` (${daysLeft >= 0 ? `${daysLeft} days left` : `${Math.abs(daysLeft)} days over`})`
                  : ''}
              </Typography>
              <LinearProgress variant='determinate' value={progress} />
            </Box>
          )}

          <Typography fontWeight={600}>
            Regulatory / Exchange / Clearing house:&nbsp;
            {lawAreas.length ? lawAreas.join(', ') : '—'}
          </Typography>

          {lawAreas.length > 0 && (
            <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap', mb: 1, mt: 1 }}>
              {lawAreas.map((la, i) => <Chip key={i} size='small' label={la} variant='outlined' />)}
            </Stack>
          )}

          <Typography fontWeight={600}>
            Issue Date:&nbsp;{issueDate ? new Date(issueDate).toLocaleDateString() : '—'}
            {criticalityLevel && (<>&nbsp;|&nbsp; Criticality:&nbsp;{criticalityLevel}</>)}
          </Typography>

          {deadlinesArr.length > 0 && (
            <>
              <Typography fontWeight={600}>
                Compliance Deadline{deadlinesArr.length > 1 ? 's' : ''}:&nbsp;
                {effectiveDeadline ? effectiveDeadline.toLocaleDateString() : '—'}
              </Typography>

              {deadlinesArr.length > 1 && (
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mt: 1 }}>
                  {deadlinesArr.map((d, i) => (
                    <Chip key={i} size="small" variant="outlined" label={d.toLocaleDateString()} />
                  ))}
                </Stack>
              )}
            </>
          )}

          <Typography fontWeight={600} gutterBottom>
            Content Type:&nbsp;{contentType}
          </Typography>

          {tags?.length > 0 && (
            <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap', mb: 2 }}>
              {tags.map((t, i) => <Chip key={i} icon={<TagIcon />} size='small' label={t} />)}
            </Stack>
          )}

          <Stack direction='row' justifyContent='center' alignItems='center' spacing={1}>
            <Typography variant='h6' fontWeight={700} align='center' sx={{ my: 2 }}>
              {title}
            </Typography>

            {isAdmin && (
              <Tooltip title='Edit update'>
                <IconButton size='small' onClick={() => onEdit(update)}>
                  <EditIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          {summary && (
            <>
              <Typography variant='subtitle1' fontWeight={700}>
                Key Details of the Update
              </Typography>
              <Typography paragraph sx={{ whiteSpace: 'pre-wrap' }}>
                {summary}
              </Typography>
            </>
          )}

          {details && (
            <Box sx={{ '& img': { maxWidth: '100%' } }} dangerouslySetInnerHTML={{ __html: details }} />
          )}

          <Divider sx={{ my: 3 }} />

          <Stack direction='row' justifyContent='space-between' alignItems='center' mb={1}>
            <Typography variant='subtitle1' fontWeight={700}>
              Tasks created for this update
            </Typography>
            <Button
              size='small'
              variant='outlined'
              onClick={() => loadLegalUpdateTrackers(luTrackersMeta.page, luTrackersMeta.limit)}
              disabled={luTrackersLoading}
            >
              Refresh
            </Button>
          </Stack>

          {luTrackersLoading && <LinearProgress sx={{ mb: 1 }} />}

          <TableContainer component={Paper} variant='outlined'>
            <Table size='small'>
              <TableHead>
                <TableRow sx={{ background: 'rgba(0,0,0,0.04)' }}>
                  <TableCell width='18%'>ID</TableCell>
                  <TableCell width='18%'>Due</TableCell>
                  <TableCell width='14%'>Status</TableCell>
                  <TableCell width='18%'>Location</TableCell>
                  <TableCell>Activity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {luTrackers.length === 0 && !luTrackersLoading ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant='body2' color='text.secondary'>
                        No tasks created yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  luTrackers.map((t, i) => (
                    <TableRow key={t._id || `${t.uniqueIdentifier || 'row'}-${i}`}>
                      <TableCell sx={{ fontWeight: 600, wordBreak: 'break-word', color: 'blue' }}>
                        {t.uniqueIdentifier ? (
                          <Box
                            component={RouterLink}
                            to={`/Edit/${encodeURIComponent(t.uniqueIdentifier)}`}
                            sx={{
                              textDecoration: 'none',
                              color: 'inherit',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            {t.uniqueIdentifier}
                          </Box>
                        ) : (
                          '—'
                        )}
                      </TableCell>

                      <TableCell>
                        {t.dueDate ? new Date(t.dueDate).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>
                        {t.status ? <Chip size='small' label={t.status} variant='outlined' /> : '—'}
                      </TableCell>
                      <TableCell sx={{ wordBreak: 'break-word' }}>
                        {t.location || '—'}
                      </TableCell>
                      <TableCell sx={{ wordBreak: 'break-word' }}>
                        {t.complianceActivity || '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {luTrackerPages > 1 && (
            <Stack alignItems='center' sx={{ mt: 2 }}>
              <Pagination
                count={luTrackerPages}
                page={luTrackersMeta.page}
                onChange={(_, p) => loadLegalUpdateTrackers(p, luTrackersMeta.limit)}
              />
            </Stack>
          )}

          {showLinks && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant='subtitle1' fontWeight={700} gutterBottom>
                Relevant Circulars / Links
              </Typography>
              <TableContainer component={Paper} variant='outlined'>
                <Table size='small'>
                  <TableHead>
                    <TableRow sx={{ background: 'rgba(0,0,0,0.04)' }}>
                      <TableCell width='30%'>Exchange</TableCell>
                      <TableCell>Link / UIN</TableCell>
                      <TableCell width='25%'>Circular Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {relatedURLs.map((r, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{r.exchange}</TableCell>
                        <TableCell sx={{ wordBreak: 'break-all' }}>
                          {/^https?:\/\//i.test(r.linkOrUIN) ? (
                            <Stack direction='row' alignItems='center' spacing={1}>
                              <img src={faviconFor(r.linkOrUIN)} alt='' width={16} height={16} />
                              <a href={r.linkOrUIN} target='_blank' rel='noreferrer'>
                                {r.linkOrUIN}
                              </a>
                              <OpenInNewIcon fontSize='inherit' sx={{ opacity: 0.6 }} />
                              <Typography variant='caption' color='text.secondary'>
                                {getDomain(r.linkOrUIN)}
                              </Typography>
                            </Stack>
                          ) : (
                            r.linkOrUIN
                          )}
                        </TableCell>
                        <TableCell>
                          {r.circularDate ? new Date(r.circularDate).toLocaleDateString() : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {showLinks && showDocs && <Divider sx={{ my: 3 }} />}

          {showDocs && (
            <>
              <Stack direction='row' justifyContent='space-between' alignItems='center' mb={1}>
                <Typography variant='subtitle1' fontWeight={700}>
                  Attached&nbsp;Documents
                </Typography>
                {isAdmin && (
                  <Stack direction='row' spacing={1}>
                    <Button size='small' startIcon={<BackupIcon />} onClick={() => fileInput.current?.click()}>
                      Upload more
                    </Button>
                    <Button
                      size='small'
                      variant='outlined'
                      startIcon={<UploadFileIcon />}
                      onClick={() => {
                        try {
                          const ws = XLSX.utils.json_to_sheet(sheetRows || []);
                          const wb = XLSX.utils.book_new();
                          XLSX.utils.book_append_sheet(wb, ws, 'Draft');
                          XLSX.writeFile(wb, `legal-update-${_id}-draft.xlsx`);
                        } catch {
                          toast.error('Could not export draft');
                        }
                      }}
                    >
                      Export Draft
                    </Button>
                  </Stack>
                )}
              </Stack>
              <input ref={fileInput} type='file' multiple hidden onChange={handleNewDocs} />
              <TableContainer component={Paper} variant='outlined'>
                <Table size='small'>
                  <TableHead>
                    <TableRow sx={{ background: 'rgba(0,0,0,0.04)' }}>
                      <TableCell width='45%'>File Name</TableCell>
                      <TableCell width='20%'>Type</TableCell>
                      <TableCell width='20%' align='right'>Size&nbsp;(KB)</TableCell>
                      <TableCell width='15%' />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documents.map(doc => (
                      <TableRow key={doc._id || doc.filePath}>
                        <TableCell sx={{ wordBreak: 'break-all' }}>{doc.fileName}</TableCell>
                        <TableCell>{doc.fileType || '—'}</TableCell>
                        <TableCell align='right'>
                          {doc.fileSize ? (doc.fileSize / 1024).toFixed(1) : '—'}
                        </TableCell>
                        <TableCell align='center'>
                          <Button size='small' onClick={() => handleDownload(doc)}>Download</Button>
                          {isAdmin && (
                            <IconButton size='small' color='error' onClick={() => handleDelete(doc)}>
                              <DeleteIcon fontSize='small' />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {myAssignees.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant='subtitle1' fontWeight={700}>
                Assigned To — {companyName || 'Your Company'}
              </Typography>
              <Stack direction='row' sx={{ my: 1 }}>
                <AvatarGroup max={8}>
                  {myAssignees.map((a, i) => (
                    <Avatar key={i} sx={{ width: 28, height: 28 }}>
                      {a.name ? initials(a.name) : (a.email ? a.email[0].toUpperCase() : '?')}
                    </Avatar>
                  ))}
                </AvatarGroup>
              </Stack>
              <TableContainer component={Paper} variant='outlined'>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {myAssignees.map((a, i) => (
                      <TableRow key={i}>
                        <TableCell>{a.name || '—'}</TableCell>
                        <TableCell>{a.email || '—'}</TableCell>
                        <TableCell>{a.role || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Frame>
      </DialogContent>

      <UploadSheetDialog
        open={previewOpen}
        legalUpdateId={_id}
        rows={sheetRows}
        onRowsChange={setSheetRows}
        rowMeta={rowMeta}
        onLoadMore={async () => {
          const nextPage = rowMeta.page + 1;
          const { data } = await api.get(`/draft-tracker/task-drafts/${_id}`, {
            params: { page: nextPage, limit: rowMeta.limit }
          });
          setSheetRows(prev => [...prev, ...data.rows]);
          setRowMeta({ page: data.page, limit: data.limit, totalRows: data.totalRows });
        }}
        onClose={() => {
          setPreviewOpen(false);
          setCreateFlow(false);
        }}
        owners={owners}
        managers={managers}
        locations={locations}
        mode={createFlow ? 'create' : 'draft'}
        submitLabel={createFlow ? 'Create Tasks' : 'Save Drafts'}
        newRowTemplate={() => ({
          dueDate: formatDueDateFromIso(effectiveDeadline),
          complianceActivity: title || '',
          lawArea: lawAreas[0],
          description: summary || '',
          category: 'Legal Update',
          sourceUrl: `${window.location.origin}/legal-update/${_id}`,
          companyname: companyName || ''
        })}
        onSubmit={async (rows) => {
          try {
            if (createFlow) {
              const base = window.location.origin;
              const normalized = (rows || []).map(r => ({
                ...r,
                proof: toBool(r.proof),
                companyname: companyName || '',
                sourceUrl: r.sourceUrl || `${base}/legal-update/${_id}`,
                legalUpdateId: _id,
              }));

              const hasOwnerOrManager = (row) => {
                const o = row.trackerOwner ?? row.trackerowner ?? '';
                const m = row.trackerManager ?? row.trackermanager ?? '';
                const oList = Array.isArray(o) ? o : String(o || '').split(/[,;]/).map(s => s.trim()).filter(Boolean);
                const mList = Array.isArray(m) ? m : String(m || '').split(/[,;]/).map(s => s.trim()).filter(Boolean);
                return oList.length > 0 || mList.length > 0;
              };
              const invalidRows = normalized.filter(r => !hasOwnerOrManager(r));
              if (invalidRows.length > 0) {
                toast.error(`At least one of Owner or Manager is required. ${invalidRows.length} row(s) have both empty.`);
                return;
              }

              await api.post('/tracker/create-tracker', { trackers: normalized });
              toast.success('Tasks created');
              setPreviewOpen(false);
              setCreateFlow(false);
              loadLegalUpdateTrackers(1, luTrackersMeta.limit);
            } else {
              await api.post('/draft-tracker/task-drafts', { legalUpdate: _id, rows });
              toast.success('Draft saved');
            }
          } catch (err) {
            const d = err.response?.data || {};
            const msg = d.trackerIndex ? `Row ${d.trackerIndex}: ${d.message}` : d.message || 'Operation failed';
            toast.error(msg);
          }
        }}
      />
    </Dialog>
  );
}
